import axios from "axios";

// import router from "@/router";
import { useAuthStore } from "@/domain/Auth/infrastructure/stores/authStore";

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 10000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Add a flag to prevent infinite refresh loops
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
  refreshSubscribers.map((cb) => cb(token));
  refreshSubscribers = [];
};

instance.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    if (authStore.isAuthenticated) {
      config.headers.Authorization = `Bearer ${authStore.token} `;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// const notFoundExceptions = [
//   "/auth/login",
//   "/404",
//   "notFound",  // route name
// ]

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const authStore = useAuthStore();
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes("/auth/login") || originalRequest.url?.includes("/profiles/refresh-token")) {
        // Don't try to refresh on login or refresh endpoints
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(instance(originalRequest));
          });
        });
      }

      if (authStore.isAuthenticated) {
        isRefreshing = true;

        try {
          const response = await authStore.refreshToken();
          const newToken = response.token;

          isRefreshing = false;
          onRefreshed(newToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];

          // Clear auth data and reload on refresh failure
          authStore.clearAuthData();
          location.reload();
          return Promise.reject(refreshError);
        }
      }
    }

    // Handle other error cases
    switch (error.response?.status) {
      case 401:
        if (useAuthStore().isAuthenticated) {
          useAuthStore().clearAuthData();
          location.reload();
        }
        break;
      default:
        console.error("An unexpected error occurred:", error);
        break;
    }

    return Promise.reject(error);
  }
);

export default instance;
